import {
  forwardRef, useEffect, useId, useMemo, useRef, useState,
  type FocusEvent, type KeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cx } from './cx'
import { useOverlayEscapeLayer, useOverlayPortalTarget } from './OverlayPortal'
import s from './DatePicker.module.css'

export interface DatePickerProps {
  /** Canonical date-only value. It is never converted through a JS instant. */
  value?: string | null
  onChange: (value: string) => void
  onBlur?: () => void
  id?: string
  name?: string
  locale?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  autoFocus?: boolean
  clearable?: boolean
  className?: string
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  /** Host focus grammar after a valid Tab commit (for grid/inline editing). */
  onTabNavigate?: (reverse: boolean) => void
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
  'data-testid'?: string
}

type PlainDate = { year: number; month: number; day: number }

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export function parseIsoDate(value: string): PlainDate | null {
  const match = ISO_DATE.exec(value)
  if (!match) return null
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
  return validDate(date) ? date : null
}

export function toIsoDate(date: PlainDate): string {
  return `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

function validDate(date: PlainDate): boolean {
  if (date.year < 1 || date.year > 9999 || date.month < 1 || date.month > 12 || date.day < 1) return false
  const candidate = new Date(Date.UTC(date.year, date.month - 1, date.day))
  return candidate.getUTCFullYear() === date.year
    && candidate.getUTCMonth() === date.month - 1
    && candidate.getUTCDate() === date.day
}

function asUtcDate(date: PlainDate): Date {
  return new Date(Date.UTC(date.year, date.month - 1, date.day, 12))
}

function fromUtcDate(date: Date): PlainDate {
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}

function addDays(date: PlainDate, amount: number): PlainDate {
  const next = asUtcDate(date)
  next.setUTCDate(next.getUTCDate() + amount)
  return fromUtcDate(next)
}

function today(): PlainDate {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
}

function localeDigits(locale: string): Map<string, string> {
  const map = new Map<string, string>()
  for (let digit = 0; digit < 10; digit += 1) {
    map.set(new Intl.NumberFormat(locale, { useGrouping: false }).format(digit), String(digit))
  }
  return map
}

function asciiDigits(value: string, locale: string): string {
  const digits = localeDigits(locale)
  return [...value].map((character) => digits.get(character) ?? character).join('')
}

function dateOrder(locale: string): Array<'day' | 'month' | 'year'> {
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .formatToParts(new Date(Date.UTC(2001, 10, 22)))
    .filter((part): part is Intl.DateTimeFormatPart & { type: 'day' | 'month' | 'year' } =>
      part.type === 'day' || part.type === 'month' || part.type === 'year')
    .map((part) => part.type)
}

export function parseDateInput(value: string, locale: string): string | null {
  const raw = asciiDigits(value.trim(), locale)
  const iso = parseIsoDate(raw)
  if (iso) return toIsoDate(iso)
  const numbers = raw.match(/\d+/g)
  if (!numbers || numbers.length !== 3) return null
  const order = dateOrder(locale)
  const parts: Partial<PlainDate> = {}
  order.forEach((part, index) => { parts[part] = Number(numbers[index]) })
  const candidate = parts as PlainDate
  return validDate(candidate) ? toIsoDate(candidate) : null
}

function displayDate(value: string, locale: string): string {
  const date = parseIsoDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
  }).format(asUtcDate(date))
}

function inputPlaceholder(locale: string): string {
  const labels = { day: 'DD', month: 'MM', year: 'YYYY' }
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    .formatToParts(new Date(Date.UTC(2001, 10, 22)))
    .map((part) => part.type in labels ? labels[part.type as keyof typeof labels] : part.value)
    .join('')
}

function monthGrid(year: number, month: number, weekStartsOn: 0 | 1): PlainDate[] {
  const first = new Date(Date.UTC(year, month - 1, 1, 12))
  const leading = (first.getUTCDay() - weekStartsOn + 7) % 7
  return Array.from({ length: 42 }, (_, index) => addDays({ year, month, day: 1 }, index - leading))
}

function defaultWeekStart(locale: string): 0 | 1 {
  const region = /-([A-Z]{2})(?:-|$)/i.exec(locale)?.[1]?.toUpperCase()
  return region === 'US' || region === 'CA' ? 0 : 1
}

/**
 * One date-only editor for every Marble host. The value crossing this boundary
 * is always `YYYY-MM-DD`; locale changes presentation and parsing only. A date
 * is deliberately never round-tripped through midnight in a timezone.
 */
export function DatePicker({
  value = '', onChange, onBlur, id: idProp, name, locale: localeProp,
  required, disabled, readOnly, autoFocus, clearable = !required, className, onKeyDown, onTabNavigate,
  ...aria
}: DatePickerProps) {
  const generatedId = useId()
  const id = idProp ?? generatedId
  const dialogId = `${id}-calendar`
  const locale = localeProp ?? (typeof navigator === 'undefined' ? 'en-GB' : navigator.language)
  const canonical = parseIsoDate(value ?? '') ? String(value) : ''
  const selected = parseIsoDate(canonical)
  const [draft, setDraft] = useState(() => displayDate(canonical, locale))
  const [invalid, setInvalid] = useState(false)
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState<PlainDate>(() => selected ?? today())
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const overlayPortalTarget = useOverlayPortalTarget()

  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraft(displayDate(canonical, locale))
  }, [canonical, locale])

  useEffect(() => {
    if (!open) return
    const reposition = () => {
      const rect = rootRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(336, window.innerWidth - 16)
      setPosition({
        top: Math.min(rect.bottom + 8, window.innerHeight - 390),
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      })
    }
    const closeOutside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!rootRef.current?.contains(target) && !dialogRef.current?.contains(target)) setOpen(false)
    }
    const closeFromKeyboard = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeFromKeyboard)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeFromKeyboard)
    }
  }, [open])

  const commitDraft = () => {
    const blank = draft.trim() === ''
    if (blank && clearable) {
      setInvalid(false)
      if (canonical) onChange('')
      return true
    }
    const parsed = parseDateInput(draft, locale)
    if (!parsed) {
      setInvalid(true)
      return false
    }
    setInvalid(false)
    setDraft(displayDate(parsed, locale))
    if (parsed !== canonical) onChange(parsed)
    return true
  }

  const choose = (date: PlainDate) => {
    const next = toIsoDate(date)
    onChange(next)
    setDraft(displayDate(next, locale))
    setInvalid(false)
    setCursor(date)
    setOpen(false)
    inputRef.current?.focus()
  }

  const show = () => {
    if (disabled || readOnly) return
    setCursor(selected ?? today())
    setOpen(true)
  }

  const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget as Node | null
    const valid = commitDraft()
    if (next && (rootRef.current?.contains(next) || dialogRef.current?.contains(next))) return
    if (valid) onBlur?.()
  }

  return (
    <div ref={rootRef} className={cx(s.root, invalid && s.invalid, className)}>
      <input ref={inputRef} id={id} name={name} value={draft} autoFocus={autoFocus}
        className={s.input} inputMode="numeric" autoComplete="off"
        placeholder={inputPlaceholder(locale)} required={required} disabled={disabled} readOnly={readOnly}
        aria-invalid={invalid || aria['aria-invalid']} aria-describedby={aria['aria-describedby']}
        aria-label={aria['aria-label']} aria-labelledby={aria['aria-labelledby']}
        data-testid={aria['data-testid']}
        onChange={(event) => { setDraft(event.target.value); setInvalid(false) }}
        onBlur={onInputBlur}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && event.altKey) { event.preventDefault(); show() }
          if (event.key === 'Enter') { event.preventDefault(); if (commitDraft()) setOpen(false) }
          if (event.key === 'Escape' && open) { event.preventDefault(); setOpen(false) }
          if (event.key === 'Tab' && onTabNavigate) {
            event.preventDefault()
            if (commitDraft()) onTabNavigate(event.shiftKey)
          }
          onKeyDown?.(event)
        }} />
      <button ref={triggerRef} type="button" className={s.trigger} disabled={disabled || readOnly}
        aria-label="Choose date" aria-haspopup="dialog" aria-expanded={open} aria-controls={dialogId}
        onClick={() => open ? setOpen(false) : show()}>
        <CalendarDays aria-hidden="true" size={18} />
      </button>
      {invalid && <span className={s.srOnly} role="alert">Enter a valid date in {inputPlaceholder(locale)} format.</span>}
      {open && typeof document !== 'undefined' && createPortal(
        <DateCalendar id={dialogId} ref={dialogRef} locale={locale} cursor={cursor} selected={selected}
          position={position} onCursor={setCursor} onChoose={choose} onClose={() => {
            setOpen(false)
            triggerRef.current?.focus()
          }} onClear={clearable ? () => {
            onChange('')
            setDraft('')
            setInvalid(false)
            setOpen(false)
            inputRef.current?.focus()
          } : undefined} />,
        overlayPortalTarget ?? document.body,
      )}
    </div>
  )
}

const DateCalendar = forwardRef<HTMLDivElement, {
  id: string
  locale: string
  cursor: PlainDate
  selected: PlainDate | null
  position: { top: number; left: number }
  onCursor: (date: PlainDate) => void
  onChoose: (date: PlainDate) => void
  onClose: () => void
  onClear?: () => void
}>(function DateCalendar({ id, locale, cursor, selected, position, onCursor, onChoose, onClose, onClear }, ref) {
  useOverlayEscapeLayer()
  const weekStartsOn = defaultWeekStart(locale)
  const days = useMemo(() => monthGrid(cursor.year, cursor.month, weekStartsOn), [cursor.year, cursor.month, weekStartsOn])
  const monthNames = useMemo(() => Array.from({ length: 12 }, (_, month) =>
    new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2020, month, 1)))), [locale])
  const weekdayNames = useMemo(() => {
    const sunday = new Date(Date.UTC(2023, 0, 1))
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(sunday)
      day.setUTCDate(day.getUTCDate() + ((index + weekStartsOn) % 7))
      return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(day)
    })
  }, [locale, weekStartsOn])
  const current = today()
  const initialCursor = useRef(cursor)
  const same = (a: PlainDate | null, b: PlainDate) => !!a && toIsoDate(a) === toIsoDate(b)
  const moveMonth = (amount: number) => {
    const next = new Date(Date.UTC(cursor.year, cursor.month - 1 + amount, 1, 12))
    onCursor({ year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: 1 })
  }
  const moveFocus = (date: PlainDate) => {
    onCursor(date)
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-picker-day="${toIsoDate(date)}"]`)?.focus())
  }
  useEffect(() => {
    document.getElementById(id)
      ?.querySelector<HTMLElement>(`[data-picker-day="${toIsoDate(initialCursor.current)}"]`)
      ?.focus()
    // Initial focus belongs in the selected/current day. Subsequent cursor
    // movement is focused by `moveFocus`; refocusing after every render would
    // steal focus from the month/year controls.
  }, [id])
  const keyDay = (event: KeyboardEvent<HTMLButtonElement>, date: PlainDate) => {
    let next: PlainDate | null = null
    if (event.key === 'ArrowRight') next = addDays(date, 1)
    if (event.key === 'ArrowLeft') next = addDays(date, -1)
    if (event.key === 'ArrowDown') next = addDays(date, 7)
    if (event.key === 'ArrowUp') next = addDays(date, -7)
    if (event.key === 'Home') next = addDays(date, -((asUtcDate(date).getUTCDay() - weekStartsOn + 7) % 7))
    if (event.key === 'End') next = addDays(date, 6 - ((asUtcDate(date).getUTCDay() - weekStartsOn + 7) % 7))
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      const amount = (event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1)
      const shifted = new Date(Date.UTC(date.year, date.month - 1 + amount, 1, 12))
      const max = new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0)).getUTCDate()
      next = { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: Math.min(date.day, max) }
    }
    if (next) { event.preventDefault(); moveFocus(next) }
  }

  return (
    <div ref={ref} id={id} role="dialog" aria-modal="false" aria-label="Choose date"
      className={s.popover} style={{ top: position.top, left: position.left }}>
      <div className={s.header}>
        <button type="button" className={s.nav} aria-label="Previous month" onClick={() => moveMonth(-1)}><ChevronLeft size={18} /></button>
        <div className={s.period}>
          <select aria-label="Month" value={cursor.month} onChange={(event) => onCursor({ ...cursor, month: Number(event.target.value), day: 1 })}>
            {monthNames.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
          </select>
          <input aria-label="Year" inputMode="numeric" value={cursor.year} onChange={(event) => {
            const year = Number(event.target.value)
            if (year >= 1 && year <= 9999) onCursor({ ...cursor, year, day: 1 })
          }} />
        </div>
        <button type="button" className={s.nav} aria-label="Next month" onClick={() => moveMonth(1)}><ChevronRight size={18} /></button>
        <button type="button" className={s.close} aria-label="Close calendar" onClick={onClose}><X size={17} /></button>
      </div>
      <div className={s.weekdays} aria-hidden="true">
        {weekdayNames.map((day) => <span key={day}>{day}</span>)}
      </div>
      <div className={s.days} role="grid" aria-label={`${monthNames[cursor.month - 1]} ${cursor.year}`}>
        {days.map((date) => {
          const iso = toIsoDate(date)
          const inMonth = date.month === cursor.month
          return <button key={iso} type="button" role="gridcell" data-picker-day={iso}
            className={cx(s.day, !inMonth && s.outside, same(selected, date) && s.selected, same(current, date) && s.todayDay)}
            tabIndex={same(cursor, date) ? 0 : -1} aria-selected={same(selected, date)}
            aria-label={new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }).format(asUtcDate(date))}
            onKeyDown={(event) => keyDay(event, date)} onClick={() => onChoose(date)}>{date.day}</button>
        })}
      </div>
      <div className={s.footer}>
        {onClear && <button type="button" className={s.action} onClick={onClear}>Clear</button>}
        <button type="button" className={s.actionPrimary} onClick={() => onChoose(current)}>Today</button>
      </div>
    </div>
  )
})
